import { useEffect, useMemo, useRef, useState } from 'react'
import { useI18n } from '../../i18n/I18nProvider'
import { useDerivation } from '../../hooks/useDerivation'
import { decodeRecipe, recipeUrl } from '../../crypto/recipe'
import {
  DEFAULT_COST_PROFILE,
  DEFAULT_POLICY,
  MAX_COUNTER,
  MIN_COUNTER,
  PRESETS,
  PRESET_ORDER,
  matchPreset,
  type CostProfileId,
  type DerivationInput,
  type Policy,
  type PresetId,
} from '../../crypto/spec'
import { policyTag } from '../../crypto/salt'
import { Button, Chip, Disclosure, Field, IconButton, Switch } from '../ui/primitives'
import {
  AlertIcon,
  EyeIcon,
  EyeOffIcon,
  KeyIcon,
  SlidersIcon,
  TrashIcon,
} from '../ui/icons'
import { Fingerprint } from './Fingerprint'
import { AdvancedOptions } from './AdvancedOptions'
import { ResultPanel } from './ResultPanel'

/** Assinatura das entradas — usada só para detectar que o resultado ficou velho. */
function signature(input: DerivationInput): string {
  return [
    input.master,
    input.service,
    input.login,
    input.counter,
    input.cost,
    policyTag(input.policy),
  ].join(' ')
}

export function GeneratorCard() {
  const { t } = useI18n()
  const { status, result, error, derive, reset } = useDerivation()

  const [master, setMaster] = useState('')
  const [service, setService] = useState('')
  const [login, setLogin] = useState('')
  const [counter, setCounter] = useState(1)
  const [policy, setPolicy] = useState<Policy>(DEFAULT_POLICY)
  const [cost, setCost] = useState<CostProfileId>(DEFAULT_COST_PROFILE)

  const [showMaster, setShowMaster] = useState(false)
  const [showFingerprint, setShowFingerprint] = useState(true)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [touched, setTouched] = useState(false)

  const derivedSignature = useRef<string | null>(null)
  const masterRef = useRef<HTMLInputElement>(null)

  // Uma receita no fragmento preenche tudo menos o segredo.
  //
  // Também escutamos `hashchange`: colar um link de receita na barra de
  // endereços da aba já aberta muda só o fragmento e NÃO recarrega a página —
  // sem isso, o link pareceria não funcionar. As âncoras da própria página
  // (#gerador, #faq) não são afetadas porque `decodeRecipe` devolve null para
  // qualquer fragmento que não seja uma receita.
  useEffect(() => {
    function applyFromHash() {
      const recipe = decodeRecipe(window.location.hash)
      if (!recipe) return
      setService(recipe.service)
      setLogin(recipe.login)
      setCounter(recipe.counter)
      setPolicy(recipe.policy)
      setCost(recipe.cost)
      setAdvancedOpen(matchPreset(recipe.policy) === null)
      masterRef.current?.focus()
    }
    applyFromHash()
    window.addEventListener('hashchange', applyFromHash)
    return () => window.removeEventListener('hashchange', applyFromHash)
  }, [])

  const input: DerivationInput = useMemo(
    () => ({ master, service, login, counter, policy, cost }),
    [master, service, login, counter, policy, cost],
  )

  const activePreset = matchPreset(policy)
  const masterError = touched && master.length === 0 ? t.gen.validation.masterRequired : undefined
  const serviceError =
    touched && service.trim().length === 0 ? t.gen.validation.serviceRequired : undefined
  const canSubmit = master.length > 0 && service.trim().length > 0

  const stale = result !== null && derivedSignature.current !== signature(input)

  function handleSubmit() {
    setTouched(true)
    if (!canSubmit) return
    derivedSignature.current = signature(input)
    derive(input)
  }

  function applyPreset(id: PresetId) {
    setPolicy({ ...PRESETS[id] })
  }

  function handleClear() {
    setMaster('')
    setService('')
    setLogin('')
    setCounter(1)
    setPolicy(DEFAULT_POLICY)
    setCost(DEFAULT_COST_PROFILE)
    setShowMaster(false)
    setTouched(false)
    derivedSignature.current = null
    reset()
    // Um fragmento de receita deixado na barra de endereços reapareceria ao
    // recarregar — limpar tudo tem que limpar isso também.
    if (window.location.hash) {
      history.replaceState(null, '', window.location.pathname + window.location.search)
    }
    masterRef.current?.focus()
  }

  return (
    <section
      id="gerador"
      className="surface-card scroll-mt-20 p-4 sm:p-6"
      aria-labelledby="gerador-titulo"
    >
      <header className="mb-5">
        <h2 id="gerador-titulo" className="text-xl font-semibold tracking-tight text-fg">
          {t.gen.title}
        </h2>
        <p className="mt-1 text-sm text-fg-muted">{t.gen.subtitle}</p>
      </header>

      <div className="flex flex-col gap-4">
        <Field
          ref={masterRef}
          label={t.gen.master.label}
          placeholder={t.gen.master.placeholder}
          hint={t.gen.master.hint}
          error={masterError}
          type={showMaster ? 'text' : 'password'}
          value={master}
          onChange={(event) => setMaster(event.target.value)}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          data-1p-ignore=""
          data-lpignore="true"
          data-bwignore="true"
          trailing={
            <div className="flex items-center gap-1">
              {showFingerprint && <Fingerprint master={master} />}
              <IconButton
                label={showMaster ? t.gen.master.hide : t.gen.master.show}
                onClick={() => setShowMaster((value) => !value)}
              >
                {showMaster ? (
                  <EyeOffIcon className="size-5" />
                ) : (
                  <EyeIcon className="size-5" />
                )}
              </IconButton>
            </div>
          }
        />

        {showFingerprint && master.length > 0 && (
          <p className="-mt-2 text-xs text-fg-subtle">
            {t.gen.fingerprint.hint} <span className="text-warn">{t.gen.fingerprint.privacy}</span>
          </p>
        )}

        {/* Serviço e login são curtos: lado a lado a partir do tablet, para o
            cartão não virar uma coluna interminável em telas largas. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t.gen.service.label}
            placeholder={t.gen.service.placeholder}
            hint={t.gen.service.hint}
            error={serviceError}
            value={service}
            onChange={(event) => setService(event.target.value)}
            type="text"
            inputMode="url"
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />

          <Field
            label={t.gen.login.label}
            placeholder={t.gen.login.placeholder}
            hint={t.gen.login.hint}
            value={login}
            onChange={(event) => setLogin(event.target.value)}
            type="text"
            inputMode="email"
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-[auto_1fr] sm:items-start sm:gap-6">
        {/* Versão */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="ard-counter" className="text-sm font-medium text-fg">
            {t.gen.counter.label}
          </label>
          <div className="flex items-center gap-2">
            <IconButton
              label={t.gen.counter.decrease}
              disabled={counter <= MIN_COUNTER}
              onClick={() => setCounter((value) => Math.max(MIN_COUNTER, value - 1))}
              className="border border-border bg-surface-2 disabled:opacity-40"
            >
              <span className="text-xl leading-none">−</span>
            </IconButton>
            <input
              id="ard-counter"
              type="number"
              inputMode="numeric"
              min={MIN_COUNTER}
              max={MAX_COUNTER}
              value={counter}
              onChange={(event) => {
                const parsed = Number.parseInt(event.target.value, 10)
                if (Number.isFinite(parsed)) {
                  setCounter(Math.min(MAX_COUNTER, Math.max(MIN_COUNTER, parsed)))
                }
              }}
              className="h-11 w-20 rounded-xl border border-border bg-surface-2 text-center font-mono text-base text-fg tabular-nums outline-none focus:border-accent focus:ring-2 focus:ring-accent/25"
            />
            <IconButton
              label={t.gen.counter.increase}
              disabled={counter >= MAX_COUNTER}
              onClick={() => setCounter((value) => Math.min(MAX_COUNTER, value + 1))}
              className="border border-border bg-surface-2 disabled:opacity-40"
            >
              <span className="text-xl leading-none">+</span>
            </IconButton>
          </div>
          <p className="text-xs text-fg-subtle">{t.gen.counter.hint}</p>
        </div>

        {/* Formato — min-w-0 é obrigatório: itens de grid têm min-width auto, e
            sem isso a régua de chips rolável esticaria a coluna inteira. */}
        <div className="flex min-w-0 flex-col gap-2">
          <span className="text-sm font-medium text-fg">{t.gen.preset.label}</span>
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            {PRESET_ORDER.map((id) => (
              <Chip key={id} selected={activePreset === id} onClick={() => applyPreset(id)}>
                {t.gen.preset.options[id]}
              </Chip>
            ))}
            {activePreset === null && (
              <Chip selected onClick={() => setAdvancedOpen(true)}>
                {t.gen.preset.custom}
              </Chip>
            )}
          </div>
          <p className="text-xs text-fg-subtle">
            {activePreset ? t.gen.preset.descriptions[activePreset] : t.gen.advanced.warning}
          </p>
        </div>
        </div>

        <Disclosure
          open={advancedOpen}
          onToggle={() => setAdvancedOpen((value) => !value)}
          label={advancedOpen ? t.gen.advanced.hide : t.gen.advanced.show}
          icon={<SlidersIcon className="size-4" />}
        >
          <div className="flex flex-col gap-4">
            <AdvancedOptions
              policy={policy}
              cost={cost}
              onPolicyChange={setPolicy}
              onCostChange={setCost}
            />
            <Switch
              checked={showFingerprint}
              onChange={setShowFingerprint}
              label={t.gen.fingerprint.toggle}
              hint={t.gen.fingerprint.hint}
            />
          </div>
        </Disclosure>
      </div>

      {/* No celular a ação principal fica grudada na base da viewport enquanto
          o cartão rola — o polegar sempre alcança "Gerar". Em telas maiores o
          cartão inteiro cabe na tela, e uma barra fixa só cobriria campos. */}
      <div className="sticky bottom-0 z-10 -mx-4 mt-4 border-t border-border bg-surface/95 px-4 py-3 supports-[backdrop-filter]:bg-surface/80 supports-[backdrop-filter]:backdrop-blur-xl sm:static sm:-mx-6 sm:border-t-0 sm:bg-transparent sm:px-6 sm:backdrop-blur-none">
        <div className="flex gap-2">
          <Button
            variant="primary"
            size="lg"
            className="flex-1"
            onClick={handleSubmit}
            disabled={status === 'working'}
            aria-busy={status === 'working'}
          >
            <KeyIcon className="size-5" />
            {status === 'working' ? t.gen.working : t.gen.submit}
          </Button>
          <IconButton
            label={t.gen.clear}
            onClick={handleClear}
            className="size-14 border border-border bg-surface-2"
          >
            <TrashIcon className="size-5" />
          </IconButton>
        </div>

        {status === 'working' && (
          <div className="mt-2">
            <div className="h-1 overflow-hidden rounded-full bg-surface-2">
              <div className="progress-sweep accent-gradient h-full w-1/3 rounded-full" />
            </div>
            <p className="mt-1.5 text-xs text-fg-subtle">{t.gen.workingNote}</p>
          </div>
        )}
      </div>

      <div className="mt-4">
        {status === 'error' && error && (
          <div
            role="alert"
            className="animate-rise rounded-2xl border border-danger/40 bg-danger/5 p-4"
          >
            <p className="flex items-center gap-2 text-sm font-semibold text-danger">
              <AlertIcon className="size-4" />
              {t.gen.error.title}
            </p>
            <p className="mt-1 text-sm text-fg-muted">{t.gen.error[error.kind]}</p>
            <p className="mt-1 font-mono text-xs break-all text-fg-subtle">{error.message}</p>
          </div>
        )}

        {status !== 'error' && result && (
          <ResultPanel
            password={result.password}
            elapsedMs={result.elapsedMs}
            policy={policy}
            stale={stale}
            recipeUrl={recipeUrl(
              { service, login, counter, policy, cost },
              window.location.origin + window.location.pathname,
            )}
          />
        )}
      </div>
    </section>
  )
}
